import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@domun/component-library";
import { PhotoAiResultsModalStyled } from "./photo-ai-results-modal.styled";
import { ImageProductForm } from "@dashboard/modules/products/types/product-form.types";

interface PhotoAiResultsModalProps {
  open: boolean;
  loading: boolean;
  adding: boolean;
  isAvailable: boolean;
  reference: ImageProductForm | null;
  relatedUrls: string[];
  selectedUrls: string[];
  getPreviewSrc: (image: ImageProductForm) => string;
  onToggle: (url: string) => void;
  onAdd: () => void;
  onAcquire: () => void;
  onClose: () => void;
}

const PhotoAiResultsModal: FC<PhotoAiResultsModalProps> = ({
  open,
  loading,
  adding,
  isAvailable,
  reference,
  relatedUrls,
  selectedUrls,
  getPreviewSrc,
  onToggle,
  onAdd,
  onAcquire,
  onClose,
}) => {
  const { t } = useTranslation();

  if (!open) return null;

  const referenceSrc = reference ? getPreviewSrc(reference) : "";

  return (
    <Modal
      title=""
      show={open}
      onClose={onClose}
      content={{
        className: "fadeInDown",
        styles: { overflow: "auto", width: "560px", maxHeight: "85vh" },
      }}
      header={{
        styles: {
          borderBottom: 0,
          boxShadow: "none",
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          zIndex: 2,
        },
      }}
      body={{ styles: { padding: 0 } }}
    >
      <PhotoAiResultsModalStyled>
        <h2>{t("productComponent.product.photoAi.resultsTitle")}</h2>
        <p className="description">
          {t("productComponent.product.photoAi.resultsDescription")}
        </p>

        {loading ? (
          <p className="loader">{t("productComponent.product.photoAi.loading")}</p>
        ) : (
          <div className="grid">
            {referenceSrc && (
              <div className="thumb reference">
                <img src={referenceSrc} alt="reference" />
                <span className="badge">Ref</span>
              </div>
            )}
            {relatedUrls.map((url) => {
              const active = selectedUrls.includes(url);
              return (
                <button
                  type="button"
                  key={url}
                  className={`thumb ${active ? "active" : ""}`}
                  onClick={() => onToggle(url)}
                >
                  <img src={url} alt="" />
                </button>
              );
            })}
          </div>
        )}

        {!isAvailable ? (
          <div className="acquire">
            <p className="acquire-title">
              {t("productComponent.product.photoAi.acquireTitle")}
            </p>
            <p className="acquire-description">
              {t("productComponent.product.photoAi.acquireDescription")}
            </p>
            <button type="button" className="primary" onClick={onAcquire}>
              {t("productComponent.product.photoAi.acquireCta")}
            </button>
          </div>
        ) : (
          <div className="actions">
            <button type="button" className="secondary" onClick={onClose}>
              {t("productComponent.product.photoAi.cancel")}
            </button>
            <button
              type="button"
              className="primary"
              disabled={!selectedUrls.length || adding || loading}
              onClick={onAdd}
            >
              {adding
                ? t("productComponent.product.photoAi.adding")
                : t("productComponent.product.photoAi.add")}
            </button>
          </div>
        )}
      </PhotoAiResultsModalStyled>
    </Modal>
  );
};

export default PhotoAiResultsModal;
